import { normalizeRotation, rotationToStorageInteger } from '../../utils/rotation';
import type { GridGeometry } from '../../grid';
import { calculateTargetAngleFromTileCenters } from '../../utils/rotation';
import type { MapDefinition, QueueFrontState, QueuedIntent, TilePosition } from './types';

interface EnqueueDestinationContext {
  map: MapDefinition;
  direction: number;
  distance: (x0: number, y0: number, x1: number, y1: number) => number;
  onRejected: (reason: 'out-of-bounds' | 'not-walkable' | 'too-far') => void;
}

export class AuthoritativeQueue {
  private characterPosition: TilePosition;
  private characterRotation = 180;
  private moveDistance = 1;
  private grid: GridGeometry | null = null;
  private serverQueue: QueuedIntent[] = [];
  private localPendingQueue: QueuedIntent[] = [];
  private frontOfQueuePosition: QueueFrontState;

  constructor(initialPosition: TilePosition) {
    this.characterPosition = initialPosition;
    this.frontOfQueuePosition = { ...initialPosition, rotation: this.characterRotation };
  }

  public getCharacterPosition(): TilePosition {
    return this.characterPosition;
  }

  public getCharacterRotation(): number {
    return this.characterRotation;
  }

  public setMoveDistance(distance: number): void {
    if (Number.isFinite(distance) && distance >= 0) {
      this.moveDistance = distance;
    }
  }

  public getMoveDistance(): number {
    return this.moveDistance;
  }

  public setGrid(grid: GridGeometry): void {
    this.grid = grid;
    this.recalculateFrontOfQueue();
  }

  public getQueueFrontRotation(): number {
    return this.frontOfQueuePosition.rotation;
  }

  public getQueueFrontPosition(): QueueFrontState {
    return this.frontOfQueuePosition;
  }

  public enqueueTurn(direction: number, intentId?: string): void {
    const normalizedDirection = rotationToStorageInteger(direction);
    const { x, y } = this.frontOfQueuePosition;
    this.localPendingQueue.push({
      intentId,
      type: 'turn',
      direction: normalizedDirection, // Store normalized direction
      position: { x, y }
    });
    this.frontOfQueuePosition = { x, y, rotation: normalizedDirection };
  }

  public getPendingQueue(): ReadonlyArray<QueuedIntent> {
    return this.mergePendingQueue(this.serverQueue, this.localPendingQueue);
  }

  public getServerQueue(): ReadonlyArray<QueuedIntent> {
    return this.serverQueue;
  }

  public getQueueCursor(): TilePosition {
    return { x: this.frontOfQueuePosition.x, y: this.frontOfQueuePosition.y };
  }

  public enqueueDestination(tile: TilePosition, context: EnqueueDestinationContext, intentId?: string): boolean {
    if (typeof tile.x !== 'number' || typeof tile.y !== 'number') {
      throw new Error('[AuthoritativeQueue] Tile position must have numeric x,y coordinates');
    }

    if (this.positionsEqual(tile, this.getQueueCursor())) {
      return false;
    }

    if (!this.isWithinBounds(tile, context.map)) {
      context.onRejected('out-of-bounds');
      return false;
    }

    if (!context.map.tiles[tile.y]?.[tile.x]?.walkable) {
      context.onRejected('not-walkable');
      return false;
    }

    const { x: lastX, y: lastY } = this.frontOfQueuePosition;
    const distance = context.distance(lastX, lastY, tile.x, tile.y);
    if (distance > this.effectiveMoveDistanceForCurrentTile(context.map, { x: lastX, y: lastY })) {
      context.onRejected('too-far');
      return false;
    }

    const direction = rotationToStorageInteger(context.direction);

    this.localPendingQueue.push({
      intentId,
      type: 'moveToTile',
      position: { ...tile },
      direction,
    });

    this.frontOfQueuePosition = {
      x: tile.x,
      y: tile.y,
      rotation: direction,
    };
    return true;
  }

  public clearPendingQueue(): void {
    this.localPendingQueue = [];
    this.frontOfQueuePosition = {
      x: this.characterPosition.x,
      y: this.characterPosition.y,
      rotation: this.characterRotation,
    };
  }

  public removeOneLocalIntent(intent: QueuedIntent): boolean {
    const index = this.localPendingQueue.findIndex((localIntent) => this.intentsEqual(localIntent, intent));
    if (index === -1) {
      return false;
    }

    this.localPendingQueue.splice(index, 1);
    this.recalculateFrontOfQueue();
    return true;
  }

  public updateServerQueue(queue: QueuedIntent[]): void {
    const previousServerQueue = this.serverQueue;
    const preferredOrder = [...previousServerQueue, ...this.localPendingQueue];
    this.serverQueue = this.orderServerQueueByPreferredSequence(queue, preferredOrder);
    this.localPendingQueue = this.localPendingQueue.filter(
      (localIntent) => !this.isRepresentedInServerQueue(localIntent, this.serverQueue)
    );

    this.recalculateFrontOfQueue();
  }

  /**
   * Some server snapshots can reorder otherwise identical queue contents.
   * If every incoming intent is already known locally, preserve prior head->tail order.
   */
  private orderServerQueueByPreferredSequence(
    incoming: QueuedIntent[],
    preferred: ReadonlyArray<QueuedIntent>
  ): QueuedIntent[] {
    if (incoming.length <= 1 || preferred.length === 0) {
      return [...incoming];
    }

    const remainingIncoming = [...incoming];
    const ordered: QueuedIntent[] = [];
    for (const intent of preferred) {
      const matchIndex = remainingIncoming.findIndex((candidate) => this.intentsEqual(candidate, intent));
      if (matchIndex === -1) {
        continue;
      }
      ordered.push(remainingIncoming[matchIndex]);
      remainingIncoming.splice(matchIndex, 1);
    }

    return remainingIncoming.length === 0 ? ordered : [...incoming];
  }

  public clearServerQueue(): void {
    this.serverQueue = [];
    this.localPendingQueue = [];
    this.recalculateFrontOfQueue();
  }

  public setCharacterPosition(position: TilePosition): void {
    this.characterPosition = position;
    this.recalculateFrontOfQueue();
  }

  public commitAuthoritativeState(position: TilePosition, rotation: number, map: MapDefinition): {
    changed: boolean;
    position: TilePosition;
    rotation: number;
  } {
    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
      throw new Error('[AuthoritativeQueue] Position must have numeric x,y coordinates');
    }

    if (!this.isWithinBounds(position, map)) {
      throw new Error(
        `[AuthoritativeQueue] Server position (${position.x},${position.y}) is out of bounds (map: ${map.width}x${map.height})`
      );
    }

    const normalizedRotation = normalizeRotation(rotation);
    const posChanged = !this.positionsEqual(this.characterPosition, position);
    const rotationChanged = this.characterRotation !== normalizedRotation;
    this.characterPosition = position;
    this.characterRotation = normalizedRotation;

    // Reconciliation: if we reached the target of a local pending intent, remove ONE matching intent
    const index = this.localPendingQueue.findIndex(intent => {
      if (intent.type.toLowerCase() === 'movetotile' && 'position' in intent) {
        return this.positionsEqual(intent.position, position);
      }
      return false;
    });
    if (index !== -1) {
      this.localPendingQueue.splice(index, 1);
    }

    this.recalculateFrontOfQueue();

    if (!this.positionsEqual(this.characterPosition, position)) {
      throw new Error('[AuthoritativeQueue] Character position was not updated correctly');
    }

    return { changed: posChanged || rotationChanged, position: this.characterPosition, rotation: this.characterRotation };
  }

  private recalculateFrontOfQueue(): void {
    this.frontOfQueuePosition = this.simulateQueueState(
      this.characterPosition,
      this.characterRotation,
      this.getPendingQueue()
    );
  }

  private simulateQueueState(
    startPos: TilePosition,
    startRotation: number,
    queue: ReadonlyArray<QueuedIntent>
  ): QueueFrontState {
    let x = startPos.x;
    let y = startPos.y;
    let rotation = startRotation;

    for (const intent of queue) {
      const type = intent.type.toLowerCase();
      if (type === 'movetotile') {
        if ('position' in intent) {
          const nextPosition = intent.position;
          const storedDirection =
            'direction' in intent && typeof intent.direction === 'number'
              ? rotationToStorageInteger(intent.direction)
              : this.deriveMovementDirection({ x, y }, nextPosition, rotation);
          x = intent.position.x;
          y = intent.position.y;
          rotation = storedDirection;
        }
      } else if (type === 'turn') {
        if ('direction' in intent && typeof intent.direction === 'number') {
          rotation = rotationToStorageInteger(intent.direction);
        }
      }
    }

    return { x, y, rotation };
  }

  private isWithinBounds(tile: TilePosition, map: MapDefinition): boolean {
    return tile.x >= 0 && tile.y >= 0 && tile.x < map.width && tile.y < map.height;
  }

  private positionsEqual(a: TilePosition, b: TilePosition): boolean {
    return a.x === b.x && a.y === b.y;
  }

  private effectiveMoveDistanceForCurrentTile(map: MapDefinition, position: TilePosition): number {
    const baseMoveDistance = Math.max(1, Math.floor(this.moveDistance));
    const tile = map.tiles[position.y]?.[position.x];
    const hasPathBoost = Boolean(
      tile
      && (
        tile.terrain === 'path'
        || (typeof tile.pathValue === 'number' && tile.pathValue > 0)
      )
    );
    return hasPathBoost
      ? baseMoveDistance + 1
      : baseMoveDistance;
  }

  private deriveMovementDirection(from: TilePosition, to: TilePosition, fallbackRotation: number): number {
    if (!this.grid || this.positionsEqual(from, to)) {
      return fallbackRotation;
    }

    return rotationToStorageInteger(
      this.grid.snapFacing(
        calculateTargetAngleFromTileCenters(this.grid, from, to)
      )
    );
  }

  private intentsEqual(a: QueuedIntent, b: QueuedIntent): boolean {
    if (a.intentId && b.intentId) {
      return a.intentId === b.intentId;
    }

    if (a.type.toLowerCase() !== b.type.toLowerCase()) return false;

    if (a.type.toLowerCase() === 'movetotile') {
      const posA = (a as any).position;
      const posB = (b as any).position;
      return posA && posB && posA.x === posB.x && posA.y === posB.y;
    }

    if (a.type.toLowerCase() === 'turn') {
      return (a as any).direction === (b as any).direction;
    }

    return false;
  }

  private mergePendingQueue(
    serverQueue: ReadonlyArray<QueuedIntent>,
    localPendingQueue: ReadonlyArray<QueuedIntent>
  ): QueuedIntent[] {
    const merged = [...serverQueue];
    for (const localIntent of localPendingQueue) {
      const alreadyRepresented = serverQueue.some((serverIntent) => this.intentsEqual(serverIntent, localIntent));
      if (!alreadyRepresented) {
        merged.push(localIntent);
      }
    }
    return merged;
  }

  private isRepresentedInServerQueue(localIntent: QueuedIntent, serverQueue: ReadonlyArray<QueuedIntent>): boolean {
    if (localIntent.intentId) {
      return serverQueue.some((serverIntent) => serverIntent.intentId === localIntent.intentId);
    }
    return serverQueue.some((serverIntent) => this.intentsEqual(serverIntent, localIntent));
  }
}
